import { useState } from 'react';
import { Input, Button, Table } from 'reactstrap';

export interface CommandsEditorProps {
  value: Record<string, string[]>;
  onChange: (next: Record<string, string[]>) => void;
  readOnly?: boolean;
}

function isParametric(phrase: string): boolean {
  return /\{[a-z][a-z0-9_]*\}/i.test(phrase);
}

export function CommandsEditor({ value, onChange, readOnly = false }: CommandsEditorProps) {
  const [draft, setDraft] = useState<Record<string, string>>({});

  const renameCommand = (oldName: string, newName: string) => {
    if (readOnly) return;
    const upper = newName.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    if (upper === oldName || upper === '') return;
    if (value[upper]) return; // duplicate, ignore
    const entries = Object.entries(value).map(([k, v]) => (k === oldName ? [upper, v] : [k, v])) as [string, string[]][];
    onChange(Object.fromEntries(entries));
  };

  const removeCommand = (name: string) => {
    if (readOnly) return;
    const next = { ...value };
    delete next[name];
    onChange(next);
  };

  const addPhrase = (cmd: string, phrase: string) => {
    if (readOnly) return;
    const trimmed = phrase.trim();
    if (trimmed === '') return;
    if ((value[cmd] ?? []).includes(trimmed)) return;
    onChange({ ...value, [cmd]: [...(value[cmd] ?? []), trimmed] });
  };

  const removePhrase = (cmd: string, phrase: string) => {
    if (readOnly) return;
    onChange({ ...value, [cmd]: (value[cmd] ?? []).filter((p) => p !== phrase) });
  };

  const addCommand = () => {
    if (readOnly) return;
    let name = 'NUOVO_COMANDO';
    let i = 1;
    while (value[name]) name = `NUOVO_COMANDO_${++i}`;
    onChange({ ...value, [name]: [] });
  };

  const entries = Object.entries(value);

  return (
    <div>
      <div className="table-responsive">
      <Table className="table-sm align-middle table-striped mb-0" role="grid">
        <thead>
          <tr>
            <th style={{ width: 220 }}>Command name</th>
            <th>Phrases</th>
            <th style={{ width: 64 }}></th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([cmd, phrases]) => (
            <tr key={cmd}>
              <td>
                <Input
                  defaultValue={cmd}
                  aria-label={`command name ${cmd}`}
                  onBlur={(e) => renameCommand(cmd, e.target.value)}
                  pattern="^[A-Z][A-Z0-9_]*$"
                  readOnly={readOnly}
                />
              </td>
              <td>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                  {phrases.map((p) => (
                    <span
                      key={p}
                      style={{
                        background: 'var(--vp-surface-2)', padding: '2px 6px', borderRadius: 'var(--vp-r-sm)',
                        fontSize: 11, display: 'inline-flex', gap: 4, alignItems: 'center',
                      }}
                    >
                      {p}
                      {isParametric(p) && <span style={{ fontSize: 9, color: 'var(--vp-info)', textTransform: 'uppercase' }}>parametrico</span>}
                      <button type="button" onClick={() => removePhrase(cmd, p)} aria-label={`rimuovi phrase ${p}`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--vp-text-3)' }} disabled={readOnly}>×</button>
                    </span>
                  ))}
                  <Input
                    style={{ height: 24, fontSize: 11, minWidth: 100, width: 'auto', display: 'inline-block' }}
                    placeholder="+ aggiungi…"
                    value={draft[cmd] ?? ''}
                    onChange={(e) => { if (!readOnly) setDraft({ ...draft, [cmd]: e.target.value }); }}
                    onKeyDown={(e) => {
                      if (readOnly) return;
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addPhrase(cmd, draft[cmd] ?? '');
                        setDraft({ ...draft, [cmd]: '' });
                      }
                    }}
                    aria-label={`add phrase to ${cmd}`}
                    disabled={readOnly}
                  />
                </div>
              </td>
              <td>
                <Button type="button" color="light" size="sm" onClick={() => removeCommand(cmd)} aria-label={`rimuovi ${cmd}`} disabled={readOnly}>×</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      </div>
      <Button type="button" color="secondary" outline size="sm" onClick={addCommand} className="mt-2" disabled={readOnly}>+ aggiungi comando</Button>
    </div>
  );
}
